/**
 * SoleneLife / Cerva — Probiotic Kombucha Gummy Cart Drawer Behavior Module
 *
 * Handles:
 * - Dynamic cart items rendering with bundle pills & compare-at savings
 * - Live quantity changes and remove with undo
 * - Smart one-click bundle tier upgrades (Buy 1 -> Buy 2 Get 1 Free -> Buy 3 Get 2 Free)
 * - Free shipping and guarantee status
 * - Header cart count syncing & auto-open on add-to-cart
 * - Mobile bottom sheet drag-to-close
 */

(function () {
  'use strict';

  const container = document.querySelector('[data-cerva-cart-drawer]');
  if (!container) return;

  const config = {
    moneyFormat: container.dataset.moneyFormat || '${{amount}}',
    productHandle: container.dataset.productHandle || 'probiotic-kombucha-gummy',
    productUrl: container.dataset.productUrl || '/products/probiotic-kombucha-gummy',
    productTitle: container.dataset.productTitle || 'Probiotic Kombucha Gummy',
    productImage: container.dataset.productImage || '',
    rating: parseFloat(container.dataset.rating) || 4.7,
    reviewCount: container.dataset.reviewCount || '8,592+',
    variantBuy1: container.dataset.variantBuy1 || '46980651122849',
    variantBuy2: container.dataset.variantBuy2 || '46981891948705',
    variantBuy3: container.dataset.variantBuy3 || '46981891981473',
    ccBaseUrl: container.dataset.ccBaseUrl || 'https://checkout.solenelife.com',
    ccIdBuy1: container.dataset.ccIdBuy1 || '',
    ccIdBuy2: container.dataset.ccIdBuy2 || '',
    ccIdBuy3: container.dataset.ccIdBuy3 || '',
  };

  const ccConfig = {
    baseUrl: config.ccBaseUrl,
    shopDomain: (window.Shopify && window.Shopify.shop) || 'fzk66g-un.myshopify.com',
    variantMap: {
      '46980651122849': config.ccIdBuy1 || '1457.22946',
      '46981891948705': config.ccIdBuy2 || '1457.22947',
      '46981891981473': config.ccIdBuy3 || '1457.22948',
      '46724879974561': '1455', // SoleneLife Estriol Cream
      '46749856268449': '1454', // Free Shipping
      '46749857743009': '1456', // Standard Shipping
    },
  };

  // Diffuser kit bundle variants -> their Checkout Champ product IDs (from the
  // product page's data attributes). Only present on the diffuser page's drawer.
  if (config.variantBuy1 && config.ccIdBuy1) ccConfig.variantMap[config.variantBuy1] = config.ccIdBuy1;
  if (config.variantBuy2 && config.ccIdBuy2) ccConfig.variantMap[config.variantBuy2] = config.ccIdBuy2;
  if (config.variantBuy3 && config.ccIdBuy3) ccConfig.variantMap[config.variantBuy3] = config.ccIdBuy3;

  const refs = {
    body: container.querySelector('[data-cerva-cd-body]'),
    items: container.querySelector('[data-cerva-cd-items]'),
    count: container.querySelector('[data-cerva-cd-count]'),
    subtotal: container.querySelector('[data-cerva-cd-subtotal]'),
    savings: container.querySelector('[data-cerva-cd-savings]'),
    savingsAmount: container.querySelector('[data-cerva-cd-savings-amount]'),
    footer: container.querySelector('[data-cerva-cd-footer]'),
    empty: container.querySelector('[data-cerva-cd-empty]'),
    banner: container.querySelector('[data-cerva-cd-banner]'),
    upsellWrap: container.querySelector('[data-cerva-cd-upsell-wrap]'),
    upsellBadge: container.querySelector('[data-cerva-cd-upsell-badge]'),
    upsellCard: container.querySelector('[data-cerva-cd-upsell-card]'),
    close: container.querySelector('[data-cerva-cd-close]'),
    handle: container.querySelector('[data-cerva-cd-handle]'),
    checkout: container.querySelector('[data-cerva-cd-checkout]'),
  };

  let lastRemovedItem = null;
  let undoTimer = null;

  // ─── Money formatting ──────────────────────────────────────────────
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = parseInt(cents, 10) || 0;
    const value = (cents / 100).toFixed(2);
    const valueNoDecimals = String(Math.round(cents / 100));
    const fmt = format || config.moneyFormat;
    return fmt
      .replace('{{amount_with_comma_separator}}', value.replace('.', ','))
      .replace('{{amount_no_decimals_with_comma_separator}}', valueNoDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, '.'))
      .replace('{{amount_no_decimals}}', valueNoDecimals)
      .replace('{{amount}}', value);
  }

  // ─── Cart AJAX API ─────────────────────────────────────────────────
  const cartRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  async function fetchCart() {
    const res = await fetch(cartRoot + 'cart.json', { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('Failed to fetch cart');
    return res.json();
  }

  async function changeCart(key, quantity) {
    const res = await fetch(cartRoot + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity }),
    });
    if (!res.ok) throw new Error('Failed to update cart');
    return res.json();
  }

  async function addToCart(variantId, quantity, properties) {
    const body = { id: variantId, quantity: quantity };
    if (properties) body.properties = properties;
    const res = await fetch(cartRoot + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error('Failed to add to cart');
    return res.json();
  }

  function esc(str) {
    const div = document.createElement('div');
    div.textContent = String(str || '');
    return div.innerHTML;
  }

  // ─── Star rating HTML ──────────────────────────────────────────────
  function starRatingHtml() {
    const starSvg = `<svg width="11" height="11" viewBox="0 0 24 24" fill="#0F4B05" stroke="#0F4B05" stroke-width="1" aria-hidden="true"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg>`;
    return `<div style="display:inline-flex;gap:2px;align-items:center;">${starSvg}${starSvg}${starSvg}${starSvg}${starSvg}</div>`;
  }

  // ─── Trash icon HTML ───────────────────────────────────────────────
  function trashIconHtml() {
    return `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>`;
  }

  // ─── Check icon HTML ───────────────────────────────────────────────
  function checkIconHtml() {
    return `<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"></polyline></svg>`;
  }

  // ─── Bundle identification ─────────────────────────────────────────
  function getBundleMeta(item) {
    const vTitle = (item.variant_title || item.title || '').toLowerCase();
    const vId = String(item.variant_id || '');

    if (vId === config.variantBuy3 || vTitle.includes('buy 3') || vTitle.includes('get 2 free')) {
      return {
        tier: 3,
        supply: '5 Month Supply',
        badge: 'Best Value · Save $79.98',
        comparePrice: 19995,
      };
    }
    if (vId === config.variantBuy2 || vTitle.includes('buy 2') || vTitle.includes('get 1 free')) {
      return {
        tier: 2,
        supply: '3 Month Supply',
        badge: 'Most Popular · Save $39.99',
        comparePrice: 11997,
      };
    }
    if (vId === config.variantBuy1 || vTitle.includes('buy 1')) {
      return {
        tier: 1,
        supply: '1 Month Supply',
        badge: '',
        comparePrice: 7998,
      };
    }
    return { tier: 0, supply: '', badge: '', comparePrice: 0 };
  }

  // ─── Render: Items ─────────────────────────────────────────────────
  function renderItems(cart) {
    if (!cart.items || cart.items.length === 0) {
      refs.items.innerHTML = '';
      return;
    }

    let html = '';
    for (const item of cart.items) {
      const meta = getBundleMeta(item);
      const imgSrc = item.image
        ? `${esc(item.image)}&width=164`
        : (config.productImage ? `${esc(config.productImage)}&width=164` : '');
      const imgHtml = imgSrc
        ? `<img src="${imgSrc}" alt="${esc(item.product_title)}" width="82" height="82" loading="lazy" style="width:100%;height:100%;object-fit:cover;display:block;">`
        : '';

      let variantLabel = 'One-time order';
      if (item.variant_title && item.variant_title !== 'Default Title') {
        variantLabel = esc(item.variant_title) + (meta.supply ? ` &middot; ${meta.supply}` : '');
      } else if (meta.supply) {
        variantLabel = meta.supply;
      }

      const pillHtml = meta.badge ? `<span class="cerva-cd-item-pill">${meta.badge}</span>` : '';

      let priceHtml = '';
      const comparePrice = item.original_price > item.final_price
        ? item.original_price
        : (meta.comparePrice > item.final_price ? meta.comparePrice : 0);

      if (comparePrice > item.final_price) {
        priceHtml = `<s>${formatMoney(comparePrice)}</s>${formatMoney(item.final_price)}`;
      } else {
        priceHtml = formatMoney(item.final_price);
      }

      html += `
        <div class="cerva-cd-item" data-key="${esc(item.key)}" data-quantity="${item.quantity}" data-variant-id="${item.variant_id}">
          <div class="cerva-cd-item-img">${imgHtml}</div>
          <div class="cerva-cd-item-info">
            <div class="cerva-cd-item-title-row">
              <a href="${esc(item.url || config.productUrl)}" class="cerva-cd-item-name">${esc(item.product_title)}</a>
            </div>
            <div class="cerva-cd-item-variant">${variantLabel}</div>
            ${pillHtml}
            <div class="cerva-cd-item-rating">
              ${starRatingHtml()}
              <span>${config.rating} (${config.reviewCount})</span>
            </div>
            <div class="cerva-cd-item-bottom">
              <div class="cerva-cd-item-price">${priceHtml}</div>
              <div class="cerva-cd-item-controls">
                <div class="cerva-cd-qty">
                  <button class="cerva-cd-qty-btn" data-cerva-cd-qty-minus type="button" aria-label="Decrease quantity">&#8722;</button>
                  <span class="cerva-cd-qty-count" data-cerva-cd-qty-count>${item.quantity}</span>
                  <button class="cerva-cd-qty-btn" data-cerva-cd-qty-plus type="button" aria-label="Increase quantity">+</button>
                </div>
                <button class="cerva-cd-item-remove" data-cerva-cd-remove type="button" aria-label="Remove item">
                  ${trashIconHtml()}
                </button>
              </div>
            </div>
          </div>
        </div>`;
    }
    refs.items.innerHTML = html;
  }

  // ─── Render: Smart bundle upsell / upgrade ─────────────────────────
  function renderUpsell(cart) {
    if (!cart.items || cart.items.length === 0) {
      refs.upsellWrap.hidden = true;
      return;
    }

    const itemsMeta = cart.items.map(item => ({ item, meta: getBundleMeta(item) }));
    const hasBuy1 = itemsMeta.find(i => i.meta.tier === 1);
    const hasBuy2 = itemsMeta.find(i => i.meta.tier === 2);
    const hasBuy3 = itemsMeta.find(i => i.meta.tier === 3);

    const imgSrc = config.productImage
      ? `${esc(config.productImage)}&width=120`
      : '//solenelife.com/cdn/shop/files/hf_20260908_001426_3ed723d8-96db-438f-8b33-91f509d04283.png?v=1788845898&width=120';

    // Priority 1: Customer has Buy 1 -> Upgrade to Buy 2 Get 1 Free
    if (hasBuy1) {
      refs.upsellBadge.innerHTML = `${checkIconHtml()} <span>Most Popular Upgrade</span>`;
      refs.upsellCard.innerHTML = `
        <div class="cerva-cd-upsell-img"><img src="${imgSrc}" alt="${esc(config.productTitle)}" loading="lazy"></div>
        <div class="cerva-cd-upsell-info">
          <div class="cerva-cd-upsell-name">Upgrade to Buy 2 Get 1 Free</div>
          <div class="cerva-cd-upsell-desc">Get a 3rd pouch FREE &amp; save $39.99. 90-day microbial reset.</div>
          <div class="cerva-cd-upsell-price"><s>$119.97</s> $79.98 (+$39.99)</div>
        </div>
        <button class="cerva-cd-upsell-add" data-cerva-cd-upsell-action="upgrade" data-from-key="${esc(hasBuy1.item.key)}" data-to-variant="${config.variantBuy2}">Upgrade</button>`;
      refs.upsellWrap.hidden = false;
      return;
    }

    // Priority 2: Customer has Buy 2 Get 1 Free -> Upgrade to Buy 3 Get 2 Free
    if (hasBuy2 && !hasBuy3) {
      refs.upsellBadge.innerHTML = `${checkIconHtml()} <span>Best Value Upgrade</span>`;
      refs.upsellCard.innerHTML = `
        <div class="cerva-cd-upsell-img"><img src="${imgSrc}" alt="${esc(config.productTitle)}" loading="lazy"></div>
        <div class="cerva-cd-upsell-info">
          <div class="cerva-cd-upsell-name">Upgrade to Buy 3 Get 2 Free</div>
          <div class="cerva-cd-upsell-desc">Get 2 pouches FREE &amp; save $79.98. Maximum 5-month support.</div>
          <div class="cerva-cd-upsell-price"><s>$199.95</s> $119.97 (+$39.99)</div>
        </div>
        <button class="cerva-cd-upsell-add" data-cerva-cd-upsell-action="upgrade" data-from-key="${esc(hasBuy2.item.key)}" data-to-variant="${config.variantBuy3}">Upgrade</button>`;
      refs.upsellWrap.hidden = false;
      return;
    }

    // Customer has top tier or no tier match
    refs.upsellWrap.hidden = true;
  }

  // ─── Render: Footer ────────────────────────────────────────────────
  function renderFooter(cart) {
    const subtotal = cart.total_price;
    let originalSubtotal = 0;

    cart.items.forEach(item => {
      const meta = getBundleMeta(item);
      const comparePrice = item.original_price > item.final_price
        ? item.original_price
        : (meta.comparePrice > item.final_price ? meta.comparePrice : item.final_price);
      originalSubtotal += comparePrice * item.quantity;
    });

    const savings = originalSubtotal - subtotal;

    if (savings > 0) {
      refs.subtotal.innerHTML = `<s>${formatMoney(originalSubtotal)}</s>${formatMoney(subtotal)}`;
      if (refs.savings && refs.savingsAmount) {
        refs.savingsAmount.textContent = formatMoney(savings);
        refs.savings.hidden = false;
      }
    } else {
      refs.subtotal.textContent = formatMoney(subtotal);
      if (refs.savings) refs.savings.hidden = true;
    }

    refs.count.textContent = cart.item_count;
    updateHeaderCartCount(cart.item_count);

    if (refs.checkout) {
      refs.checkout.disabled = cart.item_count === 0;
    }
  }

  // ─── Header Cart Count sync ────────────────────────────────────────
  function updateHeaderCartCount(count) {
    const bubbleCount = document.querySelector('[data-testid="cart-bubble"]');
    const bubble = document.querySelector('.cart-bubble');
    const cartIcon = document.querySelector('.sl-site-cart-icon');

    if (bubbleCount) {
      bubbleCount.textContent = count;
      bubbleCount.classList.toggle('hidden', count === 0);
    }
    if (bubble) {
      bubble.classList.toggle('visually-hidden', count === 0);
    }
    if (cartIcon) {
      cartIcon.classList.toggle('header-actions__cart-icon--has-cart', count > 0);
    }
  }

  // ─── Render: Empty vs Filled State ─────────────────────────────────
  function renderState(cart) {
    const isEmpty = !cart.items || cart.items.length === 0;
    refs.empty.hidden = !isEmpty;
    refs.footer.hidden = isEmpty;
    refs.banner.hidden = isEmpty;
    refs.body.hidden = isEmpty;
  }

  // ─── Full Render ───────────────────────────────────────────────────
  async function fullRender(skipItems) {
    try {
      const cart = await fetchCart();
      if (!skipItems) renderItems(cart);
      renderFooter(cart);
      renderUpsell(cart);
      renderState(cart);
      return cart;
    } catch (err) {
      console.warn('[Cerva Cart Drawer] Render error:', err);
    }
  }

  // ─── Quantity changes ──────────────────────────────────────────────
  async function handleQuantityChange(key, newQuantity) {
    const itemEl = refs.items.querySelector(`[data-key="${CSS.escape(key)}"]`);
    if (itemEl) {
      const countEl = itemEl.querySelector('[data-cerva-cd-qty-count]');
      if (countEl) countEl.textContent = newQuantity;
    }

    try {
      const cart = await changeCart(key, newQuantity);
      renderItems(cart);
      renderFooter(cart);
      renderUpsell(cart);
      renderState(cart);
    } catch (err) {
      console.warn('[Cerva Cart Drawer] Quantity error:', err);
      fullRender(false);
    }
  }

  // ─── Remove with Undo ──────────────────────────────────────────────
  async function handleRemove(key) {
    const cart = await fetchCart();
    const item = cart.items.find(i => i.key === key);
    if (!item) return;

    lastRemovedItem = { ...item };

    const itemEl = refs.items.querySelector(`[data-key="${CSS.escape(key)}"]`);
    if (itemEl) itemEl.style.display = 'none';

    showUndoBanner(item.product_title);

    try {
      const updatedCart = await changeCart(key, 0);
      renderItems(updatedCart);
      renderFooter(updatedCart);
      renderUpsell(updatedCart);
      renderState(updatedCart);

      if (lastRemovedItem) {
        refs.body.hidden = false;
        refs.empty.hidden = true;
      }
    } catch (err) {
      console.warn('[Cerva Cart Drawer] Remove error:', err);
      fullRender(false);
    }
  }

  function showUndoBanner(productName) {
    const existing = container.querySelector('.cerva-cd-undo');
    if (existing) existing.remove();
    if (undoTimer) clearTimeout(undoTimer);

    const banner = document.createElement('div');
    banner.className = 'cerva-cd-undo';
    banner.innerHTML = `
      <span>Removed ${esc(productName)}</span>
      <button type="button" class="cerva-cd-undo-btn" data-cerva-cd-undo-btn>Undo</button>
    `;

    refs.body.insertBefore(banner, refs.items);

    undoTimer = setTimeout(() => {
      banner.remove();
      lastRemovedItem = null;
      fetchCart().then(renderState);
    }, 5000);
  }

  async function handleUndo() {
    if (!lastRemovedItem) return;
    const item = lastRemovedItem;
    lastRemovedItem = null;

    if (undoTimer) clearTimeout(undoTimer);
    const existing = container.querySelector('.cerva-cd-undo');
    if (existing) existing.remove();

    try {
      await addToCart(item.variant_id, item.quantity, item.properties);
      fullRender(false);
    } catch (err) {
      console.warn('[Cerva Cart Drawer] Undo error:', err);
      fullRender(false);
    }
  }

  // ─── Upgrade Upsell Action ─────────────────────────────────────────
  async function handleUpgrade(fromKey, toVariantId, btn) {
    if (btn) {
      btn.classList.add('cerva-cd-upsell-add--loading');
      btn.textContent = 'Upgrading...';
    }

    try {
      // Remove lower bundle, add higher bundle
      await changeCart(fromKey, 0);
      await addToCart(toVariantId, 1);
      await fullRender(false);
    } catch (err) {
      console.warn('[Cerva Cart Drawer] Upgrade error:', err);
      fullRender(false);
    }
  }

  // ─── CheckoutChamp Redirection ──────────────────────────────────────
  async function handleCheckoutRedirect(e) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
    }

    const btn = refs.checkout;
    if (btn) {
      btn.disabled = true;
      btn.innerHTML = '<span>Connecting to Checkout...</span>';
    }

    try {
      const cart = await fetchCart();
      if (!cart || !cart.items || cart.items.length === 0) {
        window.location.href = '/cart';
        return;
      }

      // Check if we have mapped CheckoutChamp IDs for items in cart
      const mappedProducts = [];
      let missingId = false;

      for (const item of cart.items) {
        const vId = String(item.variant_id);
        const ccId = ccConfig.variantMap[vId];
        if (ccId) {
          mappedProducts.push(`${ccId}:${item.quantity}`);
        } else {
          missingId = true;
        }
      }

      // If all items have CheckoutChamp IDs mapped, redirect directly!
      if (!missingId && mappedProducts.length > 0) {
        let targetUrl = `${ccConfig.baseUrl}/?products=${mappedProducts.join(';')}`;
        if (cart.token) {
          targetUrl += `&cartId=${encodeURIComponent(cart.token)}`;
        }
        targetUrl += `&shop=${encodeURIComponent(ccConfig.shopDomain)}`;

        // Pass stored UTM parameters & affiliate tracking
        const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'affId', 'ref'];
        const urlParams = new URLSearchParams(window.location.search);
        utmKeys.forEach((key) => {
          const val = urlParams.get(key) || sessionStorage.getItem(key);
          if (val) targetUrl += `&${encodeURIComponent(key)}=${encodeURIComponent(val)}`;
        });

        window.location.href = targetUrl;
        return;
      }

      // Otherwise try CheckoutChamp's app proxy to see if it resolved items
      try {
        const proxyRes = await fetch('/apps/checkout-redirect/cart');
        if (proxyRes.ok) {
          const cartData = await proxyRes.json();
          const proxyItems = cartData.meta_product_id_array || [];
          if (proxyItems.length > 0) {
            const productsParam = proxyItems.map((i) => `${i.id}:${i.quantity}`).join(';');
            let targetUrl = `${ccConfig.baseUrl}/?products=${productsParam}`;
            if (cart.token) targetUrl += `&cartId=${encodeURIComponent(cart.token)}`;
            targetUrl += `&shop=${encodeURIComponent(ccConfig.shopDomain)}`;
            window.location.href = targetUrl;
            return;
          }
        }
      } catch (proxyErr) {
        console.warn('[Cerva Cart Drawer] App proxy check failed:', proxyErr);
      }

      // If CheckoutChamp IDs are not configured yet, fallback to form submission or standard checkout
      const form = container.querySelector('#cerva-cd-form');
      if (form) {
        form.submit();
      } else {
        window.location.href = '/checkout';
      }
    } catch (err) {
      console.warn('[Cerva Cart Drawer] Checkout redirect error:', err);
      window.location.href = '/checkout';
    }
  }

  // ─── Drawer open/close helpers ─────────────────────────────────────
  function closeDrawer() {
    const drawer = document.getElementById('cart-drawer');
    if (drawer && typeof drawer.close === 'function') {
      drawer.close();
    }
  }

  function openDrawer() {
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    if (isMobile) {
      // Site mobile preference redirects to full cart on small mobile devices
      return;
    }
    const drawer = document.getElementById('cart-drawer');
    if (drawer && typeof drawer.open === 'function' && !drawer.hasAttribute('open')) {
      drawer.open();
    }
  }

  // ─── Event Delegation ──────────────────────────────────────────────
  function setupEventDelegation() {
    container.addEventListener('click', (e) => {
      const target = e.target;

      // Close button
      if (target.closest('[data-cerva-cd-close]')) {
        closeDrawer();
        return;
      }

      // Quantity minus
      const minusBtn = target.closest('[data-cerva-cd-qty-minus]');
      if (minusBtn) {
        const itemEl = minusBtn.closest('[data-key]');
        if (itemEl) {
          const key = itemEl.dataset.key;
          const qty = parseInt(itemEl.dataset.quantity, 10);
          if (qty > 1) {
            handleQuantityChange(key, qty - 1);
          } else {
            handleRemove(key);
          }
        }
        return;
      }

      // Quantity plus
      const plusBtn = target.closest('[data-cerva-cd-qty-plus]');
      if (plusBtn) {
        const itemEl = plusBtn.closest('[data-key]');
        if (itemEl) {
          const key = itemEl.dataset.key;
          const qty = parseInt(itemEl.dataset.quantity, 10);
          handleQuantityChange(key, qty + 1);
        }
        return;
      }

      // Remove button
      const removeBtn = target.closest('[data-cerva-cd-remove]');
      if (removeBtn) {
        const itemEl = removeBtn.closest('[data-key]');
        if (itemEl) {
          handleRemove(itemEl.dataset.key);
        }
        return;
      }

      // Undo button
      if (target.closest('[data-cerva-cd-undo-btn]')) {
        handleUndo();
        return;
      }

      // Upsell Upgrade button
      const upsellBtn = target.closest('[data-cerva-cd-upsell-action="upgrade"]');
      if (upsellBtn) {
        const fromKey = upsellBtn.dataset.fromKey;
        const toVariant = upsellBtn.dataset.toVariant;
        handleUpgrade(fromKey, toVariant, upsellBtn);
        return;
      }

      // Checkout button click
      const checkoutBtn = target.closest('[data-cerva-cd-checkout]');
      if (checkoutBtn) {
        handleCheckoutRedirect(e);
        return;
      }
    });

    // Intercept checkout form submission
    const form = container.querySelector('#cerva-cd-form');
    if (form) {
      form.addEventListener('submit', handleCheckoutRedirect, true);
    }

    // Escape key
    container.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeDrawer();
    });

    // Auto-open on Shopify standard event
    document.addEventListener('shopify:cart:lines-update', (event) => {
      const action = event.action || (event.detail && event.detail.action);
      if (action !== 'add') return;

      openDrawer();

      if (event.promise) {
        event.promise
          .then(() => fullRender(false))
          .catch((err) => {
            if (err?.name !== 'AbortError') {
              console.warn('[Cerva Cart Drawer] Cart update promise rejected:', err);
            }
          });
      } else {
        fullRender(false);
      }
    });
  }

  // ─── Mobile drag-to-close ──────────────────────────────────────────
  function setupDragToClose() {
    if (!refs.handle) return;
    const dialog = container.closest('dialog');
    if (!dialog) return;

    let startY = 0;
    let currentY = 0;
    let isDragging = false;

    refs.handle.addEventListener(
      'touchstart',
      (e) => {
        if (!window.matchMedia('(max-width: 989px)').matches) return;
        startY = e.touches[0].clientY;
        isDragging = true;
        dialog.style.transition = 'none';
      },
      { passive: true }
    );

    refs.handle.addEventListener(
      'touchmove',
      (e) => {
        if (!isDragging) return;
        currentY = e.touches[0].clientY - startY;
        if (currentY > 0) {
          dialog.style.transform = `translateY(${currentY}px)`;
        }
      },
      { passive: true }
    );

    refs.handle.addEventListener('touchend', () => {
      if (!isDragging) return;
      isDragging = false;
      dialog.style.transition = '';
      if (currentY > 100) {
        closeDrawer();
      }
      dialog.style.transform = '';
      currentY = 0;
    });
  }

  // ─── Init ──────────────────────────────────────────────────────────
  function init() {
    setupEventDelegation();
    setupDragToClose();
    fullRender(true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
